// utils/elkDevOptions.js — DEV-ONLY ELK layout sandbox store.
//
// Lets the floating <ElkDevPanel> override raw ELK "layered" options at runtime so
// we can compare layouts side by side and decide which knobs are worth shipping.
//
// Production safety: the panel is only mounted under `import.meta.env.DEV`, so in a
// production build nothing ever calls the setters — `overrides` stays {} and
// getLayoutedElements() behaves exactly as before. The useSyncExternalStore
// subscriptions elsewhere just read version 0 forever. Zero runtime impact.

let overrides = {};            // { 'elk.direction': 'RIGHT', ... }  (ELK key -> string)
let bypassLevelRows = false;   // when true, dependency view renders raw ELK positions
let version = 0;               // bumped on every change (stable snapshot for React)
const listeners = new Set();

function emit() {
  version += 1;
  for (const l of listeners) l();
}

export function getElkOverrides() { return overrides; }
export function getBypassLevelRows() { return bypassLevelRows; }
export function getElkOptionsVersion() { return version; }

// Set an ELK option. Passing '', null or undefined clears it (falls back to default).
export function setElkOption(key, value) {
  if (value === undefined || value === null || value === "") {
    if (!(key in overrides)) return;
    const next = { ...overrides };
    delete next[key];
    overrides = next;
  } else {
    const v = String(value);
    if (overrides[key] === v) return;
    overrides = { ...overrides, [key]: v };
  }
  emit();
}

export function setBypassLevelRows(v) {
  const nv = !!v;
  if (nv === bypassLevelRows) return;
  bypassLevelRows = nv;
  emit();
}

export function resetElkOptions() {
  overrides = {};
  bypassLevelRows = false;
  emit();
}

// ── User-facing "view mode" (Layered vs Compact) ─────────────────────────────
// Compact = let ELK assign layers from the edges (partitioning off) AND keep ELK's
// raw vertical positions (bypass the level-row forcing). Layered = the defaults,
// where vertical position encodes the architectural layer. These two must always
// move together — see the layout discussion. Drives the same store the dev panel
// uses, so the panel stays in sync.
export function setCompactLayout(on) {
  bypassLevelRows = !!on;
  if (on) overrides = { ...overrides, "elk.partitioning.activate": "false" };
  else {
    const next = { ...overrides };
    delete next["elk.partitioning.activate"];
    overrides = next;
  }
  emit();
}

export function getCompactLayout() {
  return bypassLevelRows && overrides["elk.partitioning.activate"] === "false";
}

export function subscribeElkOptions(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
